import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'reducers';
import {
  PAYMENT_PROVIDERS,
} from 'utils/constants';
import {
  PaymentProviderFooter,
  AdyenResultHandler,
  SkipOnboardingButton,
  usePromoCode,
  useRefund,
} from 'components/Payment';
import {
  Box,
  Heading,
  Text,
  AsyncButton,
  PanelWrapper,
  PanelContent,
  Metadata,
} from 'components';
import NotFoundView from 'views/NotFoundView/NotFoundView';
import PaymentOptionSelection, { PaymentOptions } from 'components/Payment/PaymentOptionSelection';
import { TOfferPurchasable } from 'views/Checkout/types';
import ConsentText from 'components/ConsentText';
import { getTrialPeriodInDays, calculateReducedPrice } from './utils';
import PurchaseButton from './PurchaseButton';
import OfferInfo from './OfferInfo';
import AdyenPayment from './AdyenPayment';
import { useAnalytics } from 'components/Tracking';
import { addMessage } from 'actions/messages';

interface ICheckoutViewProps {
  createAccount?: boolean;
  packageId?: string;
  promoCode?: string;
  offer?: TOfferPurchasable;
  redirectTo?: string;
  originalOfferId?: string;
}

function CheckoutView(props: ICheckoutViewProps) {
  const {
    createAccount,
    packageId = '',
    promoCode,
    offer,
    redirectTo, // user will be redirected here when press on Start watching btn on Receipt page
    originalOfferId, // an offer id to upgrade
  } = props;

  const {
    providerType,
    allowPromoCodes,
    allowUpgradePromoCodes,
    userId,
  } = useSelector(state => ({
    providerType: state.settings.features.payment?.provider?.type,
    allowPromoCodes: state.settings.features.payment?.allowPromoCodes,
    allowUpgradePromoCodes: state.settings.features.payment?.allowUpgradePromoCodes,
    userId: state.auth.userId,
  }));

  // Payment option. New or prev credit card. Only Stripe
  const [
    selectedPaymentOption,
    setSelectedPaymentOption,
  ] = React.useState<PaymentOptions>(PaymentOptions.EXISTING);

  // Do a payment by pass. Special promo code. No credit card required. Only Adyen
  const [purchaseBypass, setPurchaseBypass] = React.useState(false);

  const {
    error: refundError,
    data: refundData,
    loading: refundLoading,
  } = useRefund(packageId, originalOfferId);

  const {
    error,
    data: {
      campaignName,
      bypassPaymentMethod,
      discount,
      usps,
    },
    loading,
  } = usePromoCode(promoCode, packageId, originalOfferId);

  const isPromoCodeAllowed = allowPromoCodes && (!originalOfferId || allowUpgradePromoCodes);

  const dispatch = useDispatch();
  const analytics = useAnalytics();

  const price = offer?.priceInCents;
  const promoCodeReduced = discount?.reducedPrice?.promotionReducedAmountCents;
  const promoCodeDiscount = promoCode && discount?.reducedPrice && price !== promoCodeReduced
    ? price - promoCodeReduced : 0;
  const upgradeRefundDiscount = refundData?.reducedPrice?.refundInCents;
  const reducedPrice = calculateReducedPrice(price, upgradeRefundDiscount, promoCodeDiscount);

  useEffect(() => {
    if (
      offer
      && !refundError
      && !loading
      && !refundLoading
      && (!promoCode || error || discount)
    ) {
      analytics.onInitiateCheckout(originalOfferId ? 'upgrade' : 'checkout', {
        userId,
        id: offer.id,
        name: offer.title,
        promoCode: error ? undefined : promoCode,
        currency: offer.currency,
        price: offer.priceInCents,
        netPrice: reducedPrice,
        grossPrice: price,
      });
    }
  }, [offer, loading, refundLoading, promoCode]);

  useEffect(() => {
    if (error) {
      dispatch(addMessage({ contentId: error, duration: 0 }));
    }
  }, [error]);

  if (!offer || refundError) {
    return (
      <NotFoundView />
    );
  }

  const trialDays = 'trialPeriod' in offer && offer.trialPeriod && !bypassPaymentMethod
    ? getTrialPeriodInDays(offer.trialPeriod)
    : 0;

  return (
    <PanelWrapper className="e2e-checkout">
      <Metadata title="payment.checkoutConfirmation" />

      <PanelContent centered width="100%">
        <Box mb="medium">
          <AdyenResultHandler />
        </Box>

        <Box
          width="100%"
          maxWidth="45rem"
          textAlign="left"
        >
          <Heading
            id="payment.checkoutConfirmation"
            mb="medium"
            align="center"
          />

          <OfferInfo
            date={new Date()}
            usps={usps}
            offer={offer}
            price={price}
            packageId={packageId}
            promoCode={promoCode}
            hasDiscount={!!promoCodeDiscount || !!upgradeRefundDiscount}
            reducedPrice={reducedPrice}
            promoCodeDiscount={promoCodeDiscount}
            upgradeRefundDiscount={upgradeRefundDiscount}
            campaignName={campaignName}
            promoCodeExpiry={discount?.reducedPrice?.expiry}
            // No promoCode and trialPeriod for offer upgrade
            trialDays={originalOfferId ? 0 : trialDays}
            isPromoCodeAllowed={isPromoCodeAllowed}
            originalOfferId={originalOfferId}
          />
        </Box>
      </PanelContent>

      {/* Payment block (adyen, stripe, by pass) */}
      { !loading && !refundLoading && (
        <>
          {/* Adyen */}
          {/* Show adyen component */}
          { providerType === PAYMENT_PROVIDERS.ADYEN_DROPIN
          && (!bypassPaymentMethod || purchaseBypass)
          && !loading && !refundLoading && (
            <AdyenPayment
              createAccount={createAccount}
              packageId={packageId}
              promoCode={discount ? promoCode : undefined}
              offer={offer}
              redirectTo={redirectTo}
              bypassPaymentMethod={bypassPaymentMethod}
              originalOfferId={originalOfferId}
            />
          )}

          {/* Confirm button for purchase by pass */}
          { providerType === PAYMENT_PROVIDERS.ADYEN_DROPIN
          && bypassPaymentMethod && !purchaseBypass && (
            <Box
              mt="xlarge"
              mb="medium"
            >
              <AsyncButton type="button" variant="brand" fixedWidth onClick={() => setPurchaseBypass(true)} infinite>
                <Text id="payment.bypassCheckoutBtn" />
              </AsyncButton>
            </Box>
          )}

          {/* Stripe. Old or new credit card option */}
          <PaymentOptionSelection
            selected={selectedPaymentOption}
            onChange={setSelectedPaymentOption}
            visibility={!bypassPaymentMethod}
          />

          {/* Button that redirects user to Stripe side */}
          { providerType === PAYMENT_PROVIDERS.STRIPE && (
            <Box
              mt="xlarge"
              mb="medium"
            >
              <PurchaseButton
                id={offer.id}
                promoCode={error ? undefined : promoCode}
                bypassCheckout={bypassPaymentMethod}
                createAccount={createAccount}
                useExistingCard={selectedPaymentOption === PaymentOptions.EXISTING}
                redirectTo={redirectTo}
                originalOfferId={originalOfferId}
              />
            </Box>
          )}
        </>
      )}

      <Box
        maxWidth="45rem"
        mt="large"
        mb="small"
        textAlign="center"
      >
        <ConsentText context="checkout" />
      </Box>

      <Box my="medium" row>
        <SkipOnboardingButton />
      </Box>

      <Box my="medium">
        <PaymentProviderFooter centered />
      </Box>
    </PanelWrapper>
  );
}

export default React.memo(CheckoutView);
